/**
 * BrokerTokenGateModal
 *
 * Shown on app load (after JWT login) when the user's broker session is
 * expired or not yet generated for today.
 *
 * Behaviour:
 *   - FYERS / Zerodha (OAuth):  shows "Log in to <Broker>" → redirects browser
 *   - AngelOne (TOTP):          shows "Generate Session" → calls test-connection inline
 *   - No broker connected:      shows "Connect a Broker" → links to /broker/connect
 *   - Can be dismissed with "Remind me later" (won't show again for this session)
 *
 * After successful token generation:
 *   - Fetches and displays broker profile + available funds as confirmation
 *   - Calls onSuccess() so parent can dismiss the modal and proceed
 *
 * Props:
 *   open      boolean   whether to show the modal
 *   onSuccess ()=>void  called when token is successfully generated
 *   onDismiss ()=>void  called when user dismisses ("Remind me later")
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { brokerApi } from '../../api/brokerClient';

const BROKER_LABELS = {
  FYERS: 'FYERS', ZERODHA: 'Zerodha', ANGELONE: 'Angel One',
  KOTAKNEO: 'Kotak Neo', NUVAMA: 'Nuvama', MASTERTRUST: 'MasterTrust',
};

const OAUTH_BROKERS = new Set(['FYERS', 'ZERODHA', 'ANGELONE', 'NUVAMA', 'MASTERTRUST']);

function FundsConfirmation({ funds, profile }) {
  if (!funds && !profile) return null;
  return (
    <div className="mt-4 bg-bull/10 border border-bull/30 rounded-xl p-4 space-y-2">
      <p className="text-xs font-semibold text-bull uppercase tracking-wide">
        ✅ Session Active
      </p>
      {profile && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-txt-primary">{profile.user_name}</span>
          <span className="text-xs text-txt-muted">({profile.user_id})</span>
          {profile.email && (
            <span className="text-xs text-txt-muted truncate">{profile.email}</span>
          )}
        </div>
      )}
      {funds && (
        <div className="grid grid-cols-3 gap-3 mt-2">
          {[
            { label: 'Available',  value: funds.available_cash },
            { label: 'Used',       value: funds.used_margin    },
            { label: 'Total',      value: funds.total_balance  },
          ].map(({ label, value }) => (
            <div key={label} className="bg-bg-surface rounded-lg p-2.5 border border-bull/20">
              <p className="text-xs text-txt-secondary mb-0.5">{label}</p>
              <p className="text-sm font-semibold text-txt-primary">
                ₹{Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BrokerTokenGateModal({ open, reason, onSuccess, onDismiss }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [step, setStep] = useState('prompt'); // 'prompt' | 'success'
  const [successData, setSuccessData] = useState({ funds: null, profile: null });
  const [error, setError] = useState(null);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['brokerStatus'],
    queryFn:  brokerApi.getCurrent,
    enabled:  open,
  });

  // AngelOne: TOTP auto-refresh via test-connection
  const testMutation = useMutation({
    mutationFn: (brokerName) =>
      brokerApi.testConnection({ broker_name: brokerName }),
    onSuccess: async (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['brokerStatus'] });
        // Fetch funds + profile for confirmation
        const [funds, profile] = await Promise.allSettled([
          brokerApi.getFunds(),
          brokerApi.getProfile(),
        ]);
        setSuccessData({
          funds:   funds.status   === 'fulfilled' ? funds.value   : null,
          profile: profile.status === 'fulfilled' ? profile.value : null,
        });
        setStep('success');
      } else if (data.oauth_url) {
        window.location.href = data.oauth_url;
      } else {
        setError(data.message || 'Session generation failed. Check your credentials.');
      }
    },
    onError: (e) => setError(e?.response?.data?.detail || 'Request failed.'),
  });

  async function handleOAuthRedirect(brokerName) {
    setError(null);
    try {
      const data = await brokerApi.getOAuthUrl(brokerName);
      if (data?.oauth_url) window.location.href = data.oauth_url;
    } catch (e) {
      setError('Could not generate login URL. Check your stored API credentials.');
    }
  }

  function handleSuccess() {
    queryClient.invalidateQueries({ queryKey: ['brokerFunds'] });
    onSuccess?.();
  }

  if (!open) return null;

  const broker       = status?.active_broker;
  const tokenStatus  = status?.token_status || 'NO_BROKER';
  const isOAuth      = broker && OAUTH_BROKERS.has(broker);
  const isAutoRefresh = status?.can_auto_refresh;
  const brokerLabel  = broker ? BROKER_LABELS[broker] || broker : null;

  return (
    // Backdrop
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-bg-panel rounded-2xl shadow-modal border border-bg-border w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="bg-bg-surface border-b border-bg-border px-6 py-5">
          <h2 className="text-lg font-bold text-txt-primary">
            {step === 'success' ? 'Broker Connected ✅' : 'Broker Session Required'}
          </h2>
          <p className="text-sm text-txt-secondary mt-1">
            {step === 'success'
              ? 'Your session is active for today.'
              : 'Generate today\'s session token to enable live trading.'}
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">

          {/* Success state */}
          {step === 'success' && (
            <>
              <FundsConfirmation
                funds={successData.funds}
                profile={successData.profile}
              />
              <button
                onClick={handleSuccess}
                className="btn-primary w-full justify-center py-2.5 rounded-xl"
              >
                Continue to App →
              </button>
            </>
          )}

          {/* No broker connected */}
          {step === 'prompt' && (reason === 'no_broker' || tokenStatus === 'NO_BROKER') && !statusLoading && (
            <>
              <p className="text-sm text-txt-secondary">
                You haven't connected a broker account yet. Connect one to enable
                live order placement from your strategies.
              </p>
              <button
                onClick={() => navigate('/broker')}
                className="btn-primary block w-full text-center justify-center py-2.5 rounded-xl"
              >
                Connect a Broker
              </button>
            </>
          )}

          {/* Token expired / not set — OAuth broker (FYERS / Zerodha) */}
          {step === 'prompt' && broker && isOAuth &&
            (tokenStatus === 'EXPIRED' || tokenStatus === 'NOT_SET') && (
            <>
              <div className="bg-amber/10 border border-amber/30 rounded-xl p-3.5">
                <p className="text-sm text-amber">
                  {tokenStatus === 'EXPIRED'
                    ? `Yesterday's ${brokerLabel} session has expired.`
                    : `No active ${brokerLabel} session found.`}
                  {' '}Log in to generate today's token.
                </p>
              </div>
              {error && (
                <p className="text-xs text-bear bg-bear/10 px-3 py-2 rounded-lg">{error}</p>
              )}
              <button
                onClick={() => handleOAuthRedirect(broker)}
                className="btn-primary w-full justify-center py-3 rounded-xl"
              >
                Log in to {brokerLabel} →
              </button>
            </>
          )}

          {/* Token expired / not set — TOTP broker (AngelOne) */}
          {step === 'prompt' && broker && isAutoRefresh &&
            (tokenStatus === 'EXPIRED' || tokenStatus === 'NOT_SET') && (
            <>
              <div className="bg-amber/10 border border-amber/30 rounded-xl p-3.5">
                <p className="text-sm text-amber">
                  Your {brokerLabel} session has expired. Click below to generate
                  a fresh session using your stored TOTP key — no browser login needed.
                </p>
              </div>
              {error && (
                <p className="text-xs text-bear bg-bear/10 px-3 py-2 rounded-lg">{error}</p>
              )}
              <button
                onClick={() => testMutation.mutate(broker)}
                disabled={testMutation.isPending}
                className="btn-primary w-full justify-center py-3 rounded-xl"
              >
                {testMutation.isPending ? 'Generating Session...' : `Generate ${brokerLabel} Session`}
              </button>
            </>
          )}

          {/* Token expired / not set — manual broker (no OAuth, no TOTP auto-refresh) */}
          {step === 'prompt' && broker && !isOAuth && !isAutoRefresh &&
            (tokenStatus === 'EXPIRED' || tokenStatus === 'NOT_SET') && (
            <>
              <div className="bg-amber/10 border border-amber/30 rounded-xl p-3.5">
                <p className="text-sm text-amber">
                  {tokenStatus === 'EXPIRED'
                    ? `Your ${brokerLabel} session has expired.`
                    : `No active ${brokerLabel} session found.`}
                  {' '}Go to Broker Settings to generate today's token.
                </p>
              </div>
              <button
                onClick={() => navigate('/broker')}
                className="btn-primary block w-full text-center justify-center py-3 rounded-xl"
              >
                Go to {brokerLabel} Settings →
              </button>
            </>
          )}

          {/* Loading */}
          {statusLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="w-6 h-6 border-2 border-amber border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Dismiss */}
          {step === 'prompt' && (
            <button
              onClick={onDismiss}
              className="w-full text-sm text-txt-muted hover:text-txt-secondary py-1.5 transition-colors"
            >
              Remind me later (live orders will be disabled)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
